export async function extractInvoiceData(storagePath, mediaType, categoryHints) {
  const res = await fetch('/api/scan-invoice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storagePath, mediaType, categoryHints })
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error || `Server error ${res.status}`);
  }

  return res.json();
}

export async function extractInvoiceDataBase64(base64Data, mediaType, categoryHints) {
  const res = await fetch('/api/scan-invoice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ base64Data, mediaType, categoryHints })
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error || `Server error ${res.status}`);
  }

  return res.json();
}

export async function extractInvoiceDataMulti(pages, categoryHints) {
  const res = await fetch('/api/scan-invoice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pages, categoryHints })
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error || `Server error ${res.status}`);
  }

  return res.json();
}

// --- Ingredient category mapping for cross-category rejection ---
const ING_CATEGORY_MAP = {
  'Protein': 'protein', 'Produce': 'produce', 'Dairy': 'dairy',
  'Bakery': 'bakery', 'Condiments': 'condiments', 'Syrups': 'beverages',
  'Beverages': 'beverages', 'Teas': 'beverages', 'Supplies': 'supplies',
  'Bottled': 'beverages',
};

function ingCategory(ing) {
  return ING_CATEGORY_MAP[ing.cat] || ing.cat?.toLowerCase() || '';
}

// Map invoice categories to broad groups for cross-category rejection
function invCategoryGroup(cat) {
  if (!cat) return '';
  const c = cat.toLowerCase();
  if (c.includes('dairy')) return 'dairy';
  if (c.includes('produce')) return 'produce';
  if (c.includes('meat') || c.includes('protein') || c.includes('seafood')) return 'protein';
  if (c.includes('frozen')) return 'protein'; // frozen is often protein/food
  if (c.includes('disposable') || c.includes('janitorial') || c.includes('paper')) return 'supplies';
  if (c.includes('beverage') || c.includes('coffee') || c.includes('tea')) return 'beverages';
  if (c.includes('bakery')) return 'bakery';
  return 'grocery'; // grocery dry, oils, etc.
}

// Broad groups that should never match each other
const INCOMPATIBLE_GROUPS = new Set([
  'protein-supplies', 'supplies-protein',
  'dairy-supplies', 'supplies-dairy',
  'produce-supplies', 'supplies-produce',
  'beverages-supplies', 'supplies-beverages',
  'protein-beverages', 'beverages-protein',
  'dairy-beverages', 'beverages-dairy',
  'produce-beverages', 'beverages-produce',
  'bakery-supplies', 'supplies-bakery',
]);

// Strip vendor product codes (e.g. "20ZPET24LS"), standalone numbers, and size tokens from a description
// so "20ZPET24LS DASANI" → "dasani" for matching purposes
function stripCodes(str){
  return (str||'').toLowerCase()
    .replace(/\d+\.?\d*z?(pet|can|psl|gl|gal|btl|ct|pk)\d*[a-z]*/gi,' ') // Coke-style codes: 20ZPET24LS, 18.5ZPSL12LS
    .replace(/\d+[./]?\d*\s*(oz|lb|gal|ct|doz|qt|pt|ml|kg|g|cs|ea)\b/gi,' ') // size tokens: 24OZ, 5LB
    .replace(/\b\d+\b/g,' ') // standalone numbers
    .replace(/[^a-z\s]/g,' ').replace(/\s+/g,' ').trim();
}

// Tokenize a string into meaningful words (3+ chars, no noise)
const STOP_WORDS = new Set(['the','and','for','with','per','each','case','pack','bag','box','can','jar','btl','pck','pkg']);
const GENERIC_WORDS = new Set(['sauce','mix','fresh','frozen','whole','sliced','diced','chopped','large','small','medium','blend','natural','organic','premium','classic','original','regular','lite','light','dry','raw','pure','plain','mini','jumbo','fancy','select','choice','style','boneless','skinless']);
function tokenize(str) {
  return str.toLowerCase()
    // Normalize pack-qty+unit: "2.5GAL"→"gal", "24OZ"→"oz" — strip leading quantity, keep unit
    // BUT keep dimension numbers: "90mm"→"90mm", "80mm"→"80mm" (product identity)
    .replace(/\d+[./]?\d*\s*(lb|oz|gal|ct|doz|qt|pt|ml|kg|g)\b/gi, (m) => m.replace(/^[\d./\s]+/, ''))
    .replace(/(\d+)\s*mm\b/gi, '$1mm')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 3 && !STOP_WORDS.has(w))
    .map(w => w.length > 3 && w.endsWith('s') ? w.slice(0,-1) : w);
}

// Extract size values from a string for size-mismatch rejection
// Returns array of {lo, hi, unit} — ranges like "1.5/2OZ" become {lo:1.5, hi:2, unit:"oz"}
function extractSizes(str) {
  const matches = [];
  // Match ranges: "1.5/2OZ", "1.5-2OZ", "3/4/5.5OZ"
  const reRange = /(\d+(?:\.\d+)?)\s*[/\-]\s*(\d+(?:\.\d+)?)\s*(oz|lb|gal|qt|pt|ml|l|ct|doz)\b/gi;
  let m;
  const used = new Set();
  while ((m = reRange.exec(str)) !== null) {
    const lo = Math.min(parseFloat(m[1]), parseFloat(m[2]));
    const hi = Math.max(parseFloat(m[1]), parseFloat(m[2]));
    matches.push({ lo, hi, unit: m[3].toLowerCase() });
    used.add(m.index);
  }
  // Match single sizes not already captured by ranges
  const reSingle = /(\d+(?:\.\d+)?)\s*(oz|lb|gal|qt|pt|ml|l|ct|doz)\b/gi;
  while ((m = reSingle.exec(str)) !== null) {
    if (used.has(m.index)) continue;
    // Check this isn't part of a range we already matched (e.g. the "2" in "1.5/2OZ")
    const before = str.slice(Math.max(0, m.index - 3), m.index);
    if (/[/\-]\s*$/.test(before)) continue;
    const v = parseFloat(m[1]);
    matches.push({ lo: v, hi: v, unit: m[2].toLowerCase() });
  }
  return matches;
}

// Check if two size sets conflict (both have sizes in the same unit but no overlap)
function sizesConflict(a, b) {
  if (!a.length || !b.length) return false;
  for (const sa of a) {
    for (const sb of b) {
      if (sa.unit !== sb.unit) continue;
      // Conflict if ranges don't overlap: sa.hi < sb.lo or sb.hi < sa.lo
      if (sa.hi < sb.lo || sb.hi < sa.lo) return true;
    }
  }
  return false;
}

export const normalizeAlias = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Strip "Pack: ..." suffix from description (defensive — OCR should separate them)
function stripPackFromDesc(item) {
  if (!item.description) return item;
  const packMatch = item.description.match(/^(.+?)\s+Pack:\s*(.+)$/i);
  if (packMatch) {
    const cleaned = { ...item, description: packMatch[1].trim() };
    if (!item.pack_size) cleaned.pack_size = packMatch[2].trim();
    return cleaned;
  }
  return item;
}

// Parse beverage product codes like "20ZPET24LS" → {oz:20, packCount:24}
function parseBevCode(desc){
  const m=(desc||'').match(/(\d+\.?\d*)Z(?:PET|CAN|PSL|GL|GAL|BTL)\s*(\d+)/i);
  if(m)return{oz:parseFloat(m[1]),packCount:parseInt(m[2])};
  return null;
}

// Post-process extracted line items: cross-check pack counts, parse bev codes
export function postProcessExtraction(lineItems){
  return (lineItems||[]).map(item=>{
    const out={...item};
    // Try beverage code parsing if pack_qty is missing or 1
    if(!out.pack_qty||out.pack_qty<=1){
      const bev=parseBevCode(out.description);
      if(bev){
        out.pack_qty=bev.packCount;
        out.pack_size=`${bev.packCount}/${bev.oz}OZ`;
        out.pack_size_source='bev_code';
      }
    }
    // Consumer-unit cross-check: if consumer_units field exists
    if(out.consumer_units&&out.qty_shipped&&out.qty_shipped>0){
      const implied=Math.round(out.consumer_units/out.qty_shipped);
      if(implied>1&&implied!==out.pack_qty){
        out.pack_qty_override=implied;
        out.pack_qty_flag=`Pack count adjusted: ${out.pack_qty||1} → ${implied} (${out.consumer_units} units ÷ ${out.qty_shipped} cases)`;
        out.pack_qty=implied;
      }
    }
    return out;
  });
}

// For catch-weight items, the stable price is unit_price (per-lb/kg).
// For regular items, the stable price is amount / qty_shipped (per-case).
function getInvoicePrice(item) {
  if (item.catch_weight != null) {
    // Catch-weight: unit_price is the per-unit rate, amount is the shipment total
    return { casePrice: item.amount, unitPrice: item.unit_price, isCatchWeight: true, weight: item.catch_weight };
  }
  const cp = (item.amount - (item.line_tax || 0)) / (item.qty_shipped || 1);
  return { casePrice: cp, unitPrice: null, isCatchWeight: false, weight: null };
}

export function matchToIngredients(lineItems, ingredients, vendor, aliasMap = new Map()) {
  const vendorUpper = (vendor || '').toUpperCase();
  return lineItems.map(rawItem => {
    const item = stripPackFromDesc(rawItem);
    const descTokens = tokenize(stripCodes(item.description));
    const brandToken = item.brand ? tokenize(item.brand) : [];
    const invGroup = invCategoryGroup(item.category);
    let bestMatch = null;
    let bestScore = 0;
    let matchMethod = 'none';
    const topCandidates = []; // collect near-misses for unmatched lines

    // STEP 0: Exact alias match (instant, skips fuzzy)
    const descSizes = extractSizes(item.description || '');
    if (aliasMap.size > 0) {
      const norm = normalizeAlias(item.description);
      const aliasIngId = aliasMap.get(norm);
      if (aliasIngId) {
        const aliasIng = ingredients.find(ing => ing.id === aliasIngId);
        if (aliasIng && sizesConflict(descSizes, extractSizes(aliasIng.n || ''))) {
          // Alias points to wrong size — skip, fall through to fuzzy
        } else if (aliasIng) {
          const ip = getInvoicePrice(item);
          // For catch-weight: compare per-unit price. For regular: compare per-case price.
          const comparePrice = ip.isCatchWeight ? ip.unitPrice : ip.casePrice;
          const sheetCompare = ip.isCatchWeight ? (aliasIng.cq > 0 ? aliasIng.cp / aliasIng.cq : 0) : aliasIng.cp;
          const pctDiff = sheetCompare > 0 ? ((comparePrice - sheetCompare) / sheetCompare * 100) : 0;
          const priceChanged = Math.abs(pctDiff) > 2;
          const sheetUnitCost = aliasIng.cq > 0 ? aliasIng.cp / aliasIng.cq : 0;
          const newUnitCost = ip.isCatchWeight ? ip.unitPrice : (aliasIng.cq > 0 ? ip.casePrice / aliasIng.cq : 0);
          return {
            ...item,
            matched_ingredient_id: aliasIng.id,
            matched_name: aliasIng.n,
            match_confidence: 1.0,
            match_method: 'alias',
            cross_distributor: false,
            price_changed: priceChanged,
            old_price: ip.isCatchWeight ? sheetCompare : aliasIng.cp,
            new_price: ip.isCatchWeight ? ip.unitPrice : ip.casePrice,
            old_unit_cost: sheetUnitCost,
            new_unit_cost: newUnitCost,
            pct_change: pctDiff,
            isCatchWeight: ip.isCatchWeight,
            catchWeightAmount: ip.isCatchWeight ? item.amount : null,
            cq: aliasIng.cq || 1,
            ppc: aliasIng.ppc || 1,
            spp: aliasIng.spp || aliasIng.cq || 1,
          };
        }
      }
    }

    // STEP 1: Exact match on vendor item number (scoped to same vendor)
    if (item.item_number) {
      const itemNum = String(item.item_number).trim();
      // First try same-vendor match
      const vinMatch = ingredients.find(ing => ing.vin && String(ing.vin).trim() === itemNum && (()=>{const d=(ing.dist||'').toUpperCase().trim();return d===vendorUpper||vendorUpper.includes(d)||d.includes(vendorUpper);})());
      if (vinMatch) { bestMatch = vinMatch; bestScore = 1.0; matchMethod = 'vin_vendor'; }
      // Fallback: any vendor with this item number
      if (!bestMatch) {
        const vinAny = ingredients.find(ing => ing.vin && String(ing.vin).trim() === itemNum);
        if (vinAny) { bestMatch = vinAny; bestScore = 1.0; matchMethod = 'vin_any'; }
      }
    }

    // STEP 2: Description fuzzy match (only if no item number match)
    const invoiceVin = item.item_number ? String(item.item_number).trim() : '';
    if (!bestMatch) for (const ing of ingredients) {
      // VIN mismatch rejection: if both have item numbers from the same vendor and they differ, skip
      // Treat ingredient with no distributor as matching the invoice's vendor
      if (invoiceVin && ing.vin) {
        const ingVin = String(ing.vin).trim();
        if (ingVin && ingVin !== invoiceVin) {
          const ingDist = (ing.dist || '').toUpperCase().trim();
          if (!ingDist || vendorUpper.includes(ingDist) || ingDist.includes(vendorUpper)) continue;
        }
      }
      // Size mismatch rejection: 20OZ ≠ 12OZ, even if names are otherwise similar
      if (sizesConflict(descSizes, extractSizes(ing.n || ''))) continue;

      // Cross-category rejection
      const ingGroup = ingCategory(ing);
      if (invGroup && ingGroup && INCOMPATIBLE_GROUPS.has(invGroup + '-' + ingGroup)) continue;

      const ingNameTokens = tokenize(ing.n);
      const ingBrandStr = tokenize(ing.b || '');
      const ingTokens = [...new Set([...ingNameTokens, ...ingBrandStr])];
      if (ingTokens.length === 0) continue;

      // Score: what fraction of ingredient name tokens appear in the invoice description
      let matchedWeight = 0;
      let totalWeight = 0;

      for (const w of ingTokens) {
        const weight = 1;
        totalWeight += weight;
        if (descTokens.includes(w)) matchedWeight += weight;
      }

      // Brand bonus: if ingredient brand matches invoice brand or description
      if (ingBrandStr.length > 0) {
        const brandInDesc = ingBrandStr.some(bt => descTokens.includes(bt) || brandToken.includes(bt));
        if (brandInDesc) {
          matchedWeight += 1.5;
          totalWeight += 1.5;
        } else {
          totalWeight += 1.5; // penalty for brand mismatch
        }
      }

      const baseScore = totalWeight > 0 ? matchedWeight / totalWeight : 0;
      // Containment boost: reward when shared tokens cover the smaller set well
      // Require at least 2 shared tokens to prevent single-word overlaps (e.g. "FRESH") from driving matches
      const sharedTokens = ingTokens.filter(w => descTokens.includes(w));
      const sharedCount = sharedTokens.length;
      const minLen = Math.min(ingTokens.length, descTokens.length);
      // Containment boost: allow 1 shared token if it's not a generic word
      const hasNonGenericShared = sharedTokens.some(w => !GENERIC_WORDS.has(w));
      const containScore = sharedCount >= 1 && minLen > 0 && hasNonGenericShared ? sharedCount / minLen : 0;
      const score = Math.max(baseScore, containScore);

      if (score >= 0.5) {
        topCandidates.push({ ingredient_id: ing.id, name: ing.n, score });
        topCandidates.sort((a, b) => b.score - a.score);
        if (topCandidates.length > 3) topCandidates.length = 3;
      }
      if (score > bestScore) {
        bestScore = score;
        bestMatch = ing;
      }
    }

    // Cross-distributor check: did the best match come from a different distributor?
    // Use contains-matching so "DENNIS" matches "DENNIS PAPER & FOOD SERVICE"
    const matchDist = (bestMatch?.dist || '').toUpperCase().trim();
    const crossDistributor = bestMatch && matchDist && !vendorUpper.includes(matchDist) && !matchDist.includes(vendorUpper);

    // Require 70% for same-distributor description matches, 90% for cross-distributor
    const descThreshold = crossDistributor ? 0.9 : 0.7;

    if (bestMatch && (matchMethod !== 'none' ? true : bestScore >= descThreshold)) {
      if (matchMethod === 'none') matchMethod = 'description';
      const ip = getInvoicePrice(item);
      // For catch-weight: compare per-unit price. For regular: compare per-case price.
      const comparePrice = ip.isCatchWeight ? ip.unitPrice : ip.casePrice;
      const sheetCompare = ip.isCatchWeight ? (bestMatch.cq > 0 ? bestMatch.cp / bestMatch.cq : 0) : bestMatch.cp;
      const pctDiff = sheetCompare > 0 ? ((comparePrice - sheetCompare) / sheetCompare * 100) : 0;
      const priceChanged = Math.abs(pctDiff) > 2;
      const sheetUnitCost = bestMatch.cq > 0 ? bestMatch.cp / bestMatch.cq : 0;
      const newUnitCost = ip.isCatchWeight ? ip.unitPrice : (bestMatch.cq > 0 ? ip.casePrice / bestMatch.cq : 0);

      if (matchMethod.startsWith('vin')) console.log(`[InvoiceMatch] Matched by item number: ${item.item_number} -> ${bestMatch.n}`);
      else console.log(`[InvoiceMatch] Matched by description fallback: ${item.description} -> ${bestMatch.n} (${(bestScore*100).toFixed(0)}%)`);

      return {
        ...item,
        matched_ingredient_id: bestMatch.id,
        matched_name: bestMatch.n,
        match_confidence: bestScore,
        match_method: matchMethod,
        cross_distributor: !!crossDistributor,
        price_changed: priceChanged,
        old_price: ip.isCatchWeight ? sheetCompare : bestMatch.cp,
        new_price: ip.isCatchWeight ? ip.unitPrice : ip.casePrice,
        old_unit_cost: sheetUnitCost,
        new_unit_cost: newUnitCost,
        pct_change: pctDiff,
        isCatchWeight: ip.isCatchWeight,
        catchWeightAmount: ip.isCatchWeight ? item.amount : null,
        cq: bestMatch.cq || 1,
        ppc: bestMatch.ppc || 1,
        spp: bestMatch.spp || bestMatch.cq || 1,
      };
    }

    console.log(`[InvoiceMatch] No match found for item ${item.item_number||'(no vin)'} ${item.description}`);
    return { ...item, matched_ingredient_id: null, matched_name: null, match_confidence: 0, match_method: 'none', price_changed: false, match_candidates: topCandidates };
  });
}

// --- COGS categorization ---
const COGS_MAP = {
  'dairy': 'Food', 'grocery dry': 'Food', 'grocery frozen': 'Food',
  'oils': 'Food', 'shortening': 'Food', 'frozen': 'Food',
  'produce': 'Food', 'protein': 'Food', 'meat': 'Food', 'seafood': 'Food',
  'bakery': 'Food', 'deli': 'Food', 'condiments': 'Food', 'spices': 'Food',
  'grocery': 'Food',
  'beverages': 'Beverages', 'beverage': 'Beverages', 'coffee': 'Beverages', 'tea': 'Beverages',
  'disposables': 'Paper Goods', 'paper': 'Paper Goods', 'supplies': 'Paper Goods',
  'janitorial': 'Paper Goods',
  'retail': 'Retail', 'bottled': 'Retail',
};

export function categorizeLine(invoiceCategory) {
  if (!invoiceCategory) return 'Food';
  const key = invoiceCategory.toLowerCase().trim();
  for (const [pattern, cogs] of Object.entries(COGS_MAP)) {
    if (key.includes(pattern)) return cogs;
  }
  return 'Food';
}
